import type { CSSProperties, ReactElement } from "react";
import { logical } from "./metrics.ts";
import { maskPath, smallDigits } from "./small-digits.ts";

/**
 * A number over art in the small digits (ADR-111's note of 2026-10-08 for P2.18-T6, the author's words that a slot's
 * count hid the item): the 3 by 5 glyphs in the element's text colour inside their closed one pixel outline in the text
 * outline colour, drawn as an SVG on the logical grid. The element also holds the number as hidden text, so its
 * `textContent` is the number.
 */

/**
 * A number in the small digits.
 *
 * @param props The `text` (digits, slashes, percent and plus signs), the element's `className` and `data-*` anchor
 *   (`kit`, `hud`), and its `place`: the offsets of the outline's box from the positioned parent, in logical pixels.
 * @returns The number's span.
 * @throws RangeError when the text holds a character the small digits lack.
 * @example
 * <SmallNumber text="20" className="ot-item-slot__count" kit="item-slot-count" place={{ right: 0, bottom: 0 }} />
 */
export function SmallNumber({ text, className, kit, hud, place }: { readonly text: string; readonly className: string; readonly kit?: string; readonly hud?: string; readonly place: { readonly left?: number; readonly top?: number; readonly right?: number; readonly bottom?: number } }): ReactElement {
  const digits = smallDigits(text);
  const style: CSSProperties = { width: logical(digits.width), height: logical(digits.height) };
  if (place.left !== undefined) {
    style.left = logical(place.left);
  }
  if (place.top !== undefined) {
    style.top = logical(place.top);
  }
  if (place.right !== undefined) {
    style.right = logical(place.right);
  }
  if (place.bottom !== undefined) {
    style.bottom = logical(place.bottom);
  }
  return (
    <span className={`ot-small-number ${className}`} data-kit={kit} data-hud={hud} aria-hidden="true" style={style}>
      <svg viewBox={`0 0 ${digits.width} ${digits.height}`} shapeRendering="crispEdges">
        <path className="ot-small-number__outline" d={maskPath(digits.outline)} />
        <path className="ot-small-number__glyphs" d={maskPath(digits.glyph)} />
      </svg>
      <span hidden>{text}</span>
    </span>
  );
}
